import { isWork, workDate } from "../../pages/pageMetadata";
import { useRef, useState, type ReactNode, type RefObject } from "react";
import { ListChecks, Plus, X } from "lucide-react";
import { BlockRenderer } from "./BlockRenderer";
import type { PageCardStyle } from "./PageCard";
import {
  parseBlockContent,
  type BlockContent,
} from "../../../lib/blockContentSchemas";
import type {
  BlockEditPatch,
  EditorAsset,
  EditorBlock,
  EditorPage,
} from "../editor.types";

type Layout = BlockContent<"library">["layout"];

/** Each layout's arrangement, and the card style its items take. */
const layouts: Record<Layout, { className: string; card: PageCardStyle }> = {
  grid: {
    className:
      "grid grid-cols-2 gap-x-[var(--site-grid-gap,1rem)] gap-y-7 @max-[220px]:grid-cols-1 @min-[600px]:grid-cols-3 @min-[600px]:gap-x-[var(--site-grid-gap,1.5rem)]",
    card: "feature",
  },
  cards: { className: "grid grid-cols-1 gap-y-9", card: "feature" },
  list: { className: "grid gap-2.5", card: "card" },
  index: { className: "border-t border-site-border", card: "link" },
};

type LibraryBlockProps = {
  block: EditorBlock;
  blocks: EditorBlock[];
  pages: EditorPage[];
  assetsById: Map<string, EditorAsset>;
  onSelectPage: (pageId: string) => void;
  editing?: boolean;
  onSave?: (patch: BlockEditPatch) => Promise<string | null>;
  onCancelEdit?: () => void;
  onDelete?: (blockId: string) => void;
  onSaveEdit?: (
    block: EditorBlock,
    patch: BlockEditPatch,
  ) => Promise<string | null>;
  onQuickUpdate?: (
    block: EditorBlock,
    patch: BlockEditPatch,
  ) => Promise<string | null>;
  /** Opens a picker of existing work beside `anchor` (the button). */
  onChooseItems?: (anchor: DOMRect) => void;
  /** Creates a new work item for this gallery. */
  onCreateItem?: () => void;
  onSelectBlock?: (sourceBlockId: string) => void;
  onOpenPage?: (page: EditorPage) => void;
  pageHref?: (page: EditorPage) => string;
  selectedBlockId?: string | null;
  selectedBlockRef?: RefObject<HTMLDivElement | null>;
  currentPageId?: string;
  bare?: boolean;
  chooseLabel?: string;
  createLabel?: string;
  /** Makes the title editable in place (the editor only). */
  onRename?: (name: string) => void;
  /** Takes an item off a Selected gallery (the editor only); the work stays. */
  onRemoveItem?: (child: EditorBlock) => void;
};

export const LibraryBlock = ({
  block,
  blocks,
  pages,
  assetsById,
  onSelectPage,
  onOpenPage,
  pageHref,
  onChooseItems,
  onCreateItem,
  bare,
  chooseLabel = "Choose work",
  createLabel = "New work item",
  onRename,
  onRemoveItem,
}: LibraryBlockProps): ReactNode => {
  const content = parseBlockContent("library", block.content);
  const work = pages.filter((p) => isWork(p) && p.id !== block.page_id);
  const sourceBlocks: EditorBlock[] =
    content.mode === "all"
      ? work.map((p, position) => ({
          ...block,
          id: `gallery-${block.id}-${p.id}`,
          source_block_id: p.source_page_id,
          block_type: "page",
          parent_source_block_id: block.source_block_id,
          position,
          target_page_source_id: p.source_page_id,
          content: { display: "card", showDescription: true },
        }))
      : blocks;
  const children = sourceBlocks
    .filter(
      (child) =>
        child.parent_source_block_id === block.source_block_id &&
        (child.block_type !== "page" ||
          pages.some(
            (page) => page.source_page_id === child.target_page_source_id,
          )),
    )
    .sort((a, b) => {
      if (content.order !== "manual") {
        const created = (block: EditorBlock) =>
          workDate(
            pages.find(
              (page) => page.source_page_id === block.target_page_source_id,
            ) ?? { seo: {} },
          );
        const aDate = created(a),
          bDate = created(b);
        if (!aDate) return bDate ? 1 : 0;
        if (!bDate) return -1;
        return content.order === "oldest"
          ? aDate.localeCompare(bDate)
          : bDate.localeCompare(aDate);
      }
      return a.position - b.position;
    });
  const layout = layouts[content.layout];
  const selected = content.mode !== "all";
  return (
    <section className="@container">
      {/* In the editor, the count leaves room for the block's options button. */}
      <header className={`mb-4 flex items-baseline justify-between gap-3 ${bare ? "" : "pr-10"}`}>
        <h3 className="site-heading min-w-0 flex-1 leading-snug heading-size-22 @min-[600px]:heading-size-24">
          {onRename ? (
            <LibraryName
              key={content.name}
              name={content.name || "Selected work"}
              onRename={onRename}
            />
          ) : (
            content.name || "Selected work"
          )}
        </h3>
        {/* The count is for the creator; visitors don't see it. */}
        {!bare && (
          <span className="shrink-0 rounded-site-pill bg-site-border px-2.5 py-0.5 text-xs whitespace-nowrap text-site-muted">
            {children.length} {children.length === 1 ? "item" : "items"}
          </span>
        )}
      </header>
      {children.length > 0 && (
        <div className={layout.className}>
          {children.map((child) => (
            <div key={child.id} className="group/item relative min-w-0">
              <BlockRenderer
                block={child}
                blocks={blocks}
                pages={pages}
                assetsById={assetsById}
                onSelectPage={onSelectPage}
                onOpenPage={onOpenPage}
                pageHref={pageHref}
                bare={bare}
                pageDisplay={layout.card}
                compact={content.layout !== "cards"}
              />
              {onRemoveItem && selected && (
                <RemoveItemButton onClick={() => onRemoveItem(child)} />
              )}
            </div>
          ))}
        </div>
      )}
      {!children.length && !bare && (
        <p className="text-sm leading-relaxed text-site-muted">
          A place for your projects, stories, or selected work.
        </p>
      )}
      {/* Selected galleries keep adding; "all" galleries fill themselves. */}
      {onCreateItem && (selected || !children.length) && (
        <div className="mt-3 flex gap-3 @max-[420px]:flex-col">
          {onChooseItems && selected && (
            <LibraryAddButton
              icon={<ListChecks size={16} aria-hidden="true" />}
              label={chooseLabel}
              onClick={onChooseItems}
            />
          )}
          <LibraryAddButton
            icon={<Plus size={16} aria-hidden="true" />}
            label={createLabel}
            onClick={() => onCreateItem()}
          />
        </div>
      )}
    </section>
  );
};

const LibraryAddButton = ({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: (anchor: DOMRect) => void;
}) => (
  <button
    className="flex min-h-11 w-full min-w-0 flex-1 items-center justify-center gap-2 rounded-site border border-dashed border-site-accent-soft p-3 text-sm font-medium text-site-accent transition-colors hover:bg-site-highlight disabled:opacity-60"
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      onClick(event.currentTarget.getBoundingClientRect());
    }}
  >
    {icon}
    {label}
  </button>
);

/** A glass × in an item's corner, shown while the item is hovered. */
const RemoveItemButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    aria-label="Remove from gallery"
    title="Remove from gallery"
    className="glass absolute top-2 right-2 z-1 grid size-7 place-items-center rounded-full border border-hazumu-white/85 text-hazumu-ink opacity-0 transition-opacity duration-120 [--glass-shadow:0_2px_8px_color-mix(in_srgb,var(--color-hazumu-ink)_14%,transparent)] group-hover/item:opacity-100 hover:bg-hazumu-white focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-terracotta"
    onMouseDown={(event) => event.stopPropagation()}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    <X size={14} aria-hidden="true" />
  </button>
);

/** The title as a borderless field; saves on Enter or blur, Escape reverts. */
const LibraryName = ({
  name,
  onRename,
}: {
  name: string;
  onRename: (name: string) => void;
}) => {
  const [draft, setDraft] = useState(name);
  const cancelled = useRef(false);
  const commit = () => {
    const next = draft.trim();
    if (!next || cancelled.current) setDraft(name);
    else if (next !== name) onRename(next);
  };
  return (
    <input
      aria-label="Gallery name"
      className="w-full min-w-0 bg-transparent p-0 outline-none"
      maxLength={100}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          cancelled.current = true;
          event.currentTarget.blur();
          cancelled.current = false;
        }
      }}
    />
  );
};
